import React, { useEffect, useState } from "react";
import styles from "./style.module.css";
import KakaoLogin from "react-kakao-login";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import AppleLogin from "react-apple-login";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { decodeToken } from "utils/decodeUtil";
import useUser from "hooks/useUser";
import { TLoginType } from "data/user";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  headerAndBottomVisibleState,
  OSState,
  signOnState,
} from "recoil/atoms";
import { MyPage } from "pages/MyPage";
import { envUtil, ScreenId } from "utils";
import useNav from "hooks/useNav";

interface LoginResponse {
  token_type: string;
  access_token: string;
  expires_in: string;
  refresh_token: string;
  refresh_token_expires_in: number;
  scope: string;
}

interface Profile {
  nickname: string;
  profile_image: string;
  thumbnail_image_url: string;
  profile_needs_agreement?: boolean;
}

interface KakaoAccount {
  profile: Profile;
  email: string;
  age_range: string;
  birthday: string;
  birthyear: string;
  gender: "female" | "male";
  phone_number: string;
  ci: string;
}

interface IKakaoSuccess {
  response: LoginResponse;
  profile?: { id: number; kakao_account: KakaoAccount };
}

export default function SignIn() {
  const { user, login, logOut } = useUser();
  const { originNavigate, navigate, getParams } = useNav();
  const { prevPage, prevParam } = getParams<{
    prevPage?: ScreenId;
    prevParam?: any;
  }>();
  const setSignOnData = useSetRecoilState(signOnState);
  const [headerAndBottomVisible, setHeaderAndBottomVisible] = useRecoilState(
    headerAndBottomVisibleState,
  );
  const { isBottom } = headerAndBottomVisible;

  const [isKakaoBrower, setKakaoBrower] = useState(false);
  const [appleDevice, setAppleDevice] = useState(false);
  const OS = useRecoilValue(OSState);

  async function checkAccount({
    id,
    token,
    type,
  }: {
    id: string;
    token: string;
    type: TLoginType;
    profileImg?: string;
  }) {
    try {
      const { message, success: loginSuccess } = await login({
        sns_id: id,
        sns_type: type,
      });
      if (loginSuccess) {
        toast.success(message);
        if (prevPage) {
          navigate({ url: prevPage, params: prevParam, replace: true });
        } else {
          originNavigate(-1);
        }
      } else {
        throw Error("로그인정보 없음");
      }
    } catch (error: any) {
      if (error.message) {
        setSignOnData({ id, token, type });
        navigate({ url: ScreenId.SIGN_ON });
        toast.error(error.message);
        console.error("error", error);
      }
    }
  }
  const kakaoSuccess = async (data: IKakaoSuccess) => {
    const kakao_id = data?.profile?.id;
    const kakao_token = data?.response.access_token;
    if (kakao_id && kakao_token) {
      checkAccount({
        id: String(kakao_id),
        token: kakao_token,
        type: "kakao",
      });
    }
  };

  const CheckGoogleAuth = async (accessToken: string) => {
    if (!accessToken) {
      return;
    }

    const response = await axios.get(
      "https://www.googleapis.com/oauth2/v2/userinfo",
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (response.status === 200) {
      const google_id = response.data?.id;
      if (google_id) {
        checkAccount({
          id: response.data.id,
          token: accessToken,
          type: "google",
        });
      }
    } else {
      toast.error("로그인 에러가 발생했습니다");
      ////도메인 생성후
      window.location.assign("https://www.makemywedding-pc.co.kr/");
    }
  };

  const Googlelogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      CheckGoogleAuth(tokenResponse.access_token);
    },
    onError: (error) => console.error(error),
  });

  function appleCallback(response: any) {
    const apple_id_token = response?.authorization?.id_token;
    if (apple_id_token) {
      const decodeData = decodeToken(apple_id_token);
      const apple_token = decodeData?.payload?.sub;
      if (apple_token) {
        checkAccount({ id: apple_token, token: apple_token, type: "apple" });
      }
    }
  }

  useEffect(() => {
    const isKakao = navigator.userAgent.match("KAKAOTALK");
    setKakaoBrower(Boolean(isKakao));
  }, []);

  useEffect(() => {
    const isAppleDevice = /Macintosh|iPhone|iPad/.test(navigator.userAgent);
    setAppleDevice(Boolean(isAppleDevice));
  }, []);

  return (
    <>
      {!user ? (
        <div className={styles.wrap}>
          <div className={styles.content}>
            <img
              className={styles.logo}
              src="/images/logo.png"
              alt="make my wedding"
            />
            <h1 className={styles.title}>
              나만의 결혼식 만들기
              <br />
              필수 웨딩 플랫폼
            </h1>

            <div className={styles.btnWrap}>
              <KakaoLogin
                token={envUtil.KAKAO.JAVASCRIPT_KEY}
                onSuccess={kakaoSuccess}
                onFail={console.error}
                onLogout={console.info}
                render={({ onClick }) => {
                  return (
                    <a
                      href="/kakaoLogin"
                      onClick={(e) => {
                        e.preventDefault();
                        onClick();
                      }}
                      className={styles.kakaoButton}
                    >
                      <img src="/images/kakao.png" alt="kakao-login" />
                      카카오로 로그인하기
                    </a>
                  );
                }}
              />
              {!isKakaoBrower ||
              (!isBottom && OS.toLowerCase() == "android") ? (
                <a
                  onClick={() => Googlelogin()}
                  className={styles.googleButton}
                >
                  <img src="/images/google.png" />
                  Google로 계속하기
                </a>
              ) : null}
              {isKakaoBrower && (
                <a
                  onClick={() => Googlelogin()}
                  className={styles.googleButton}
                >
                  <img src="/images/google.png" />
                  Google 로그인은 <br />
                  다른 브라우저로 시도해주세요.
                </a>
              )}
              {(!isBottom && OS.toLowerCase() == "ios") || appleDevice ? (
                <AppleLogin
                  clientId="co.kr.makemywedding-pc"
                  redirectURI="https://www.makemywedding-pc.co.kr"
                  usePopup={true}
                  scope="email name"
                  responseMode="query"
                  responseType="id code id_token"
                  callback={appleCallback}
                  render={(renderProps) => (
                    <a
                      href="#"
                      onClick={renderProps.onClick}
                      className={styles.appleButton}
                    >
                      <img
                        src="/images/apple.png"
                        style={{ height: "22px" }}
                        alt="apple-login"
                      />
                      Apple로 계속하기
                    </a>
                  )}
                />
              ) : null}

              <ToastContainer
                position="bottom-center"
                limit={1}
                closeButton={false}
                autoClose={2000}
                theme="light"
              />
            </div>
          </div>
        </div>
      ) : (
        // 마이페이지
        <MyPage />
      )}
    </>
  );
}
